import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  FileText,
  Upload,
  X,
} from 'lucide-react';

const getExistingFileName = (document) =>
  document?.file_record?.original_filename ||
  document?.file_record?.original_name ||
  document?.file_record?.file?.split('/').pop() ||
  document?.document_type ||
  'Document';

const StaffDocuments = ({
  formData,
  setFormData,
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const fileInputRef = useRef(null);

  const selectedFiles = useMemo(
    () =>
      (Array.isArray(formData.documents)
        ? formData.documents
        : []
      ).filter(
        (item) =>
          typeof File !== 'undefined' &&
          item instanceof File,
      ),
    [formData.documents],
  );

  const existingDocuments = Array.isArray(formData.existingDocuments)
    ? formData.existingDocuments
    : [];

  useEffect(() => {
    if (fileInputRef.current && selectedFiles.length === 0) {
      fileInputRef.current.value = '';
    }
  }, [selectedFiles.length]);

  const handleFileChange = (event) => {
    const incomingFiles = Array.from(event.target.files || []);

    setFormData((current) => ({
      ...current,
      documents: [...selectedFiles, ...incomingFiles],
    }));
  };

  const removeSelectedFile = (index) => {
    setFormData((current) => ({
      ...current,
      documents: selectedFiles.filter(
        (_, fileIndex) => fileIndex !== index,
      ),
    }));
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
      <button
        type="button"
        onClick={() => setIsOpen((current) => !current)}
        className="flex w-full items-center justify-between border-b border-gray-100 px-6 py-4 text-left"
      >
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-600 text-sm font-semibold text-white">
            5
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Documents
            </h2>
            <p className="text-xs text-gray-500">
              Optional. Upload appointment letters, certificates, and identity documents.
            </p>
          </div>
        </div>

        {isOpen ? (
          <ChevronUp className="h-5 w-5 text-gray-500" />
        ) : (
          <ChevronDown className="h-5 w-5 text-gray-500" />
        )}
      </button>

      {isOpen && (
        <div className="space-y-5 p-6">
          {existingDocuments.length > 0 && (
            <div>
              <h3 className="mb-3 text-sm font-semibold text-gray-900">
                Existing documents
              </h3>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {existingDocuments.map((document) => (
                  <div
                    key={document.id}
                    className="flex items-start gap-3 rounded-xl border border-gray-200 bg-gray-50 p-4"
                  >
                    <FileText className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-gray-900">
                        {getExistingFileName(document)}
                      </p>
                      <p className="mt-1 text-xs text-gray-500">
                        {document.document_type || 'STAFF_DOCUMENT'}
                        {document.verification_status
                          ? ` · ${document.verification_status}`
                          : ''}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="rounded-2xl border-2 border-dashed border-gray-300 p-8 text-center">
            <Upload className="mx-auto mb-4 h-10 w-10 text-gray-400" />
            <h3 className="font-medium text-gray-900">
              Select documents to upload
            </h3>
            <p className="mt-2 text-sm text-gray-500">
              PDF, JPEG, PNG, or WebP files. The files are uploaded after the staff profile is saved.
            </p>

            <input
              ref={fileInputRef}
              type="file"
              id="staffDocuments"
              multiple
              accept=".pdf,image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={handleFileChange}
            />

            <label
              htmlFor="staffDocuments"
              className="mt-5 inline-block cursor-pointer rounded-xl bg-blue-600 px-6 py-2.5 text-sm font-semibold text-white hover:bg-blue-700"
            >
              Browse Files
            </label>
          </div>

          {selectedFiles.length > 0 && (
            <div>
              <h3 className="mb-3 text-sm font-semibold text-gray-900">
                New files to upload ({selectedFiles.length})
              </h3>
              <ul className="space-y-2">
                {selectedFiles.map((file, index) => (
                  <li
                    key={`${file.name}-${file.size}-${index}`}
                    className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 p-3"
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <FileText className="h-5 w-5 shrink-0 text-blue-600" />
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-gray-800">
                          {file.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {(file.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => removeSelectedFile(index)}
                      className="rounded-lg p-2 text-gray-400 hover:bg-red-50 hover:text-red-600"
                      aria-label={`Remove ${file.name}`}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StaffDocuments;
